import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Folder, ChevronRight, Home, Loader2 } from 'lucide-react';
import { useGetRequest, usePatchRequest, usePostRequest } from '@/shared/hooks/requests.js';
import { NODE_CHILDREN, FILE, NODE_BULK_MOVE, FILES_BULK_COPY } from '@/shared/utils/urls.js';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import LoadingSpinner from '@/shared/ui/LoadingSpinner';

// One page of a level's folders (the root when `parentUuid` is null).
const folderPickerParams = (parentUuid, page) => ({
  type: 'folder',
  size: 100,
  page,
  ...(parentUuid ? { parent_uuid: parentUuid } : {}),
});

const parentOf = (node) => node?.parentUuid ?? node?.parent_uuid ?? null;

/**
 * Move (or copy) files and folders into a folder picked here.
 *
 * Single-file callers pass `file`; the multi-select toolbar passes `files`
 * (rows with a `uuid`) and/or `folderUuids`.
 */
export const MoveFileModal = ({ open, onOpenChange, file, files, folderUuids = [], mode = 'move', onMoveSuccess }) => {
  const { t } = useTranslation(['common', 'my-docs']);
  const [currentFolderUuid, setCurrentFolderUuid] = useState(null);
  const [selectedFolderUuid, setSelectedFolderUuid] = useState(null);
  const [navigationHistory, setNavigationHistory] = useState([]);

  const targets = (files && files.length ? files : file ? [file] : []);
  const isCopy = mode === 'copy';
  const isBulk = isCopy || (files && files.length > 0) || (folderUuids && folderUuids.length > 0);
  const totalCount = targets.length + (folderUuids?.length || 0);

  // Initialize history when modal opens
  useEffect(() => {
    if (open) {
      setNavigationHistory([{ uuid: null, name: t('my-docs:title') }]);
      setCurrentFolderUuid(null);
      setSelectedFolderUuid(null);
    }
  }, [open, t]);

  const fetchFolders = useGetRequest({});
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(false);

  const moveFile = usePatchRequest({});
  const bulkMove = usePostRequest({ url: NODE_BULK_MOVE });
  const bulkCopy = usePostRequest({ url: FILES_BULK_COPY });
  const busy = moveFile.loading || bulkMove.loading || bulkCopy.loading;

  // Page through the whole level so wide directories (>100 subfolders)
  // stay fully reachable as move targets.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setFoldersLoading(true);
      const acc = [];
      for (let page = 1; ; page += 1) {
        const { success, response } = await fetchFolders.request({
          url: NODE_CHILDREN,
          params: folderPickerParams(currentFolderUuid, page),
        });
        if (!success || cancelled) break;
        acc.push(...(response?.items || []));
        if (!response?.pages || page >= response.pages) break;
      }
      if (!cancelled) {
        setFolders(acc);
        setFoldersLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFolderUuid, open]);

  const handleFolderClick = (folder) => {
    setCurrentFolderUuid(folder.uuid);
    setNavigationHistory([...navigationHistory, { uuid: folder.uuid, name: folder.name }]);
  };

  const navigateTo = (index) => {
    const item = navigationHistory[index];
    setCurrentFolderUuid(item.uuid);
    setNavigationHistory(navigationHistory.slice(0, index + 1));
  };

  const handleMove = async () => {
    const fileUuids = targets.map((f) => f.uuid).filter(Boolean);

    if (isBulk) {
      if (fileUuids.length === 0 && (!folderUuids || folderUuids.length === 0)) return;

      if (isCopy) {
        // Copy is files-only (folders aren't recursively copied yet).
        const { success, response } = await bulkCopy.request({
          data: { fileUuids, targetFolderUuid: selectedFolderUuid },
        });
        if (success) {
          const done = response?.copied?.length ?? fileUuids.length;
          toast.success(t('my-docs:copy.success', { count: done }));
          onMoveSuccess?.();
          onOpenChange(false);
        }
        return;
      }

      // Move: files and folders in ONE unified request.
      const nodeUuids = [...fileUuids, ...(folderUuids || [])];
      const { success, response } = await bulkMove.request({
        data: { nodeUuids, targetParentUuid: selectedFolderUuid },
      });
      if (success) {
        const done = response?.moved?.length ?? 0;
        const invalid = response?.invalid?.length ?? 0;
        toast.success(t('my-docs:move.successCount', { count: done }));
        if (invalid > 0) {
          toast.error(t('my-docs:move.invalidTarget', { count: invalid }));
        }
        onMoveSuccess?.();
        onOpenChange(false);
      }
      return;
    }

    // Single-file move (legacy context-menu path).
    const single = targets[0];
    if (selectedFolderUuid === parentOf(single)) {
      toast.info(t('my-docs:move.alreadyThere'));
      onOpenChange(false);
      return;
    }
    const { success } = await moveFile.request({
      url: FILE.replace('{file_uuid}', single.uuid),
      data: { parentUuid: selectedFolderUuid },
    });
    if (success) {
      toast.success(t('my-docs:move.success'));
      onMoveSuccess?.();
      onOpenChange(false);
    }
  };

  const isSameFolderSingle =
    !isBulk && targets[0] && selectedFolderUuid === parentOf(targets[0]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden gap-0">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
             {isCopy ? t('my-docs:copy.title') : t('my-docs:move.title')}
          </DialogTitle>
          <div className="text-sm text-muted-foreground mt-1 truncate">
            {totalCount > 1
              ? t('my-docs:bulk.selectedCount', { count: totalCount })
              : targets[0]?.name}
          </div>
        </DialogHeader>

        <div className="px-6 py-2 border-b">
          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar py-1">
            {navigationHistory.map((item, index) => (
              <div key={item.uuid ?? 'root'} className="flex items-center shrink-0">
                 {index > 0 && <ChevronRight className="w-3 h-3 text-muted-foreground mx-1" />}
                 <button
                    onClick={() => navigateTo(index)}
                    className={cn(
                      "text-xs px-2 py-1 rounded-md hover:bg-muted transition-colors flex items-center gap-1",
                      index === navigationHistory.length - 1 ? "bg-muted font-medium text-foreground" : "text-muted-foreground"
                    )}
                 >
                   {index === 0 ? <Home className="w-3 h-3" /> : null}
                   <span>{item.name}</span>
                 </button>
              </div>
            ))}
          </div>
        </div>

        <ScrollArea className="h-[350px]">
           {foldersLoading ? (
             <div className="flex flex-col items-center justify-center h-[350px] gap-2">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                <p className="text-xs text-muted-foreground">{t('my-docs:move.loadingFolders')}</p>
             </div>
           ) : (
             <div className="p-4 space-y-1">
                {/* Option to move to current folder */}
                <button
                   onClick={() => setSelectedFolderUuid(currentFolderUuid)}
                   className={cn(
                     "w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left",
                     selectedFolderUuid === currentFolderUuid
                      ? "border-blue-500 bg-blue-50/50 dark:bg-blue-500/10"
                      : "border-transparent hover:bg-muted/50"
                   )}
                >
                  <div className={cn(
                    "w-10 h-10 rounded-lg flex items-center justify-center shrink-0",
                    selectedFolderUuid === currentFolderUuid ? "bg-blue-100 dark:bg-blue-900/30" : "bg-muted"
                  )}>
                     <Folder className={cn("w-5 h-5", selectedFolderUuid === currentFolderUuid ? "text-blue-500" : "text-slate-400")} />
                  </div>
                  <div className="flex-1 min-w-0">
                     <p className="text-sm font-semibold truncate">
                       {currentFolderUuid === null ? t('my-docs:title') : navigationHistory[navigationHistory.length - 1].name}
                     </p>
                     <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">
                       {t('my-docs:move.toThisFolder')}
                     </p>
                  </div>
                </button>

                <div className="flex items-center gap-2 py-2">
                  <div className="h-px bg-border flex-1" />
                  <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest">
                    {t('my-docs:move.subfolders')}
                  </span>
                  <div className="h-px bg-border flex-1" />
                </div>

                {folders.length === 0 ? (
                   <div className="flex flex-col items-center justify-center py-10 text-center">
                     <Folder className="w-10 h-10 text-slate-200 mb-2" />
                     <p className="text-sm text-muted-foreground">
                       {t('my-docs:move.noSubfolders')}
                     </p>
                   </div>
                ) : (
                  folders.map(folder => (
                    <div key={folder.uuid} className="flex items-center gap-1">
                      <button
                        onClick={() => setSelectedFolderUuid(folder.uuid)}
                        className={cn(
                          "flex-1 flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left group",
                          selectedFolderUuid === folder.uuid
                            ? "border-blue-500 bg-blue-50/50 dark:bg-blue-500/10"
                            : "border-transparent hover:bg-muted/30"
                        )}
                      >
                         <div className={cn(
                           "w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-transform group-hover:scale-110",
                           selectedFolderUuid === folder.uuid ? "bg-blue-100 dark:bg-blue-900/30" : "bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800"
                         )}>
                            <img src="/images/folder.svg" alt={t('my-docs:properties.folder')} className="w-6 h-6" />
                         </div>
                         <div className="flex-1 min-w-0">
                           <span className="text-sm font-medium truncate block">{folder.name}</span>
                           <span className="text-[10px] text-muted-foreground">
                             {t('mainPage:items', { count: folder.itemCount ?? folder.fileCount ?? 0 })}
                           </span>
                         </div>
                      </button>
                      <button
                         onClick={(e) => { e.stopPropagation(); handleFolderClick(folder); }}
                         className="h-12 w-10 flex items-center justify-center hover:bg-muted rounded-xl transition-colors shrink-0"
                         title={t('my-docs:move.enterFolder')}
                      >
                         <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </div>
                  ))
                )}
             </div>
           )}
        </ScrollArea>

        <DialogFooter className="p-6 border-t bg-muted/20">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full">
            {t('my-docs:btn.cancel')}
          </Button>
          <Button
            onClick={handleMove}
            disabled={busy || isSameFolderSingle}
            className="bg-brand hover:bg-[#324E8C] text-white rounded-full px-8 shadow-lg shadow-blue-500/20"
          >
            {busy
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : isCopy
                ? t('my-docs:actions.copyExecute')
                : t('my-docs:actions.moveExecute')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
